import { useEffect, useMemo, useState } from 'react';
import { Box, Icon, Image, Paragraph, UnstyledButton } from '@zillow/constellation-10';
import { IconPhotosOutline } from '@zillow/constellation-icons';
import TooltipWrapper from '../../TooltipWrapper';
import { expiredCaptionContainerStyles, imageThumbnailPlaceholderStyles } from './styles';

type Props = {
  fileName?: string;
  isImpersonator?: boolean;
  onClick(): void;
  shouldDarkenImage: boolean;
  shouldShowExpiredCaption?: boolean;
  shouldShowPlaceholder: boolean;
  size: number;
  url: string;
  impersonationTooltipText?: string;
};

const BORDER_RADIUS_TOKEN = '300';
const IMAGE_DARKEN_FILTER = 'brightness(45%)';
const EXPIRED_CAPTION_HEIGHT_RATIO = 0.5;

function getOptimizedImageUrl(url: string, size: number): string {
  const isZDocPreviewUrl = url.includes('zillowdocs') && url.includes('/image/preview');
  return isZDocPreviewUrl ? `${url}&height=${size}&width=${size}` : url;
}

export default function ImageThumbnail({
  fileName = '',
  isImpersonator = false,
  onClick,
  shouldDarkenImage,
  shouldShowExpiredCaption = false,
  shouldShowPlaceholder,
  size,
  url,
  impersonationTooltipText = 'Cannot view while impersonating',
}: Props): JSX.Element {
  const [hasImageError, setHasImageError] = useState(false);

  const displayHeight = shouldShowExpiredCaption ? size * EXPIRED_CAPTION_HEIGHT_RATIO : size;
  const shouldRoundBottomCorners = !shouldShowExpiredCaption;
  const thumbnailDimensions = {
    width: `${size}px`,
    height: `${displayHeight}px`,
  };

  const imageSrc = useMemo(() => getOptimizedImageUrl(url, size), [url, size]);

  // Reset error state when URL changes (e.g., new blob URL)
  useEffect(() => {
    setHasImageError(false);
  }, [url]);

  // Show placeholder if explicitly set OR if image failed to load (e.g., expired blob URL)
  const shouldShowPlaceholderFinal = shouldShowPlaceholder || hasImageError;

  const ariaLabel = shouldShowPlaceholderFinal
    ? `Image attachment ${fileName || 'placeholder'} (unavailable)`
    : `View image attachment ${fileName || ''} full screen`;

  const captionText = `${fileName || 'This file'} has expired and can't be viewed.`;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <TooltipWrapper
        tooltipPlacement="top-start"
        tooltipText={impersonationTooltipText}
        wrapInTooltip={isImpersonator}
      >
        <UnstyledButton
          data-testid="image-thumbnail-btn"
          onClick={onClick}
          aria-label={ariaLabel}
          aria-disabled={shouldShowPlaceholderFinal}
          disabled={shouldShowPlaceholderFinal}
          style={thumbnailDimensions}
          css={{
            ...(shouldShowPlaceholderFinal ? {} : { cursor: 'pointer' }),
          }}
        >
          {shouldShowPlaceholderFinal ? (
            <Box
              data-testid="image-thumbnail-placeholder"
              style={thumbnailDimensions}
              css={{
                ...imageThumbnailPlaceholderStyles,
                borderBottomLeftRadius: shouldRoundBottomCorners ? BORDER_RADIUS_TOKEN : '0',
                borderBottomRightRadius: shouldRoundBottomCorners ? BORDER_RADIUS_TOKEN : '0',
              }}
            >
              <Icon size="lg" css={{ color: 'border.action.neutral.default' }}>
                <IconPhotosOutline />
              </Icon>
            </Box>
          ) : (
            <Image
              data-testid="image-thumbnail"
              alt={`Image attachment thumbnail for ${fileName}`}
              src={imageSrc}
              height={size}
              width={size}
              style={thumbnailDimensions}
              onError={() => setHasImageError(true)}
              css={{
                borderRadius: BORDER_RADIUS_TOKEN,
                objectFit: 'cover',
                filter: shouldDarkenImage ? IMAGE_DARKEN_FILTER : 'none',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            />
          )}
        </UnstyledButton>
      </TooltipWrapper>

      {shouldShowExpiredCaption && (
        <Box data-testid="image-expired-caption" css={expiredCaptionContainerStyles}>
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle' }}>
            {captionText}
          </Paragraph>
        </Box>
      )}
    </Box>
  );
}
